/**
 * Reports Page
 *
 * Main page for generating and displaying reports with flexible filtering and display options.
 * Supports both legacy report generation and new template-based reports.
 */

import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Card,
  Button,
  Select,
  MultiSelect,
  DateRangePicker,
  Input,
  Modal,
  Tabs,
  Badge,
  Alert,
  Checkbox,
} from "../../shared";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
  subMonths,
  subQuarters,
  subYears,
  getQuarter,
} from "date-fns";
import ReportTable from "../../components/ReportTable";
import InvoiceAnalyticsPanel from "../../components/InvoiceAnalyticsPanel";
import ProfitLossReport from "../../components/ProfitLossReport";
import {
  useGenerateReportMutation,
  useGetReportTemplatesQuery,
  useCreateReportTemplateMutation,
  useSaveReportMutation,
} from "../../redux/services/reports/reportsApi";
import { useGetEmployeesQuery } from "../../redux/services/employees/employeesApi";
import { useGetClientsQuery } from "../../redux/services/clients/clientsApi";
import { useGetOrganizationsQuery } from "../../redux/services/organizations/organizationsApi";
import { toast } from "react-toastify";
import {
  DocumentChartBarIcon,
  PlusIcon,
  PlayIcon,
  BookmarkIcon,
  ClockIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../../hooks/useAuth";
import { useConfig } from "../../hooks";
import { SidebarContainer } from "../../components";
import LegRouteAudit from "./LegRouteAudit";

/**
 * Normalize a client filter into unique ids.
 * Accepts a legacy single id string or an array of ids.
 * An empty result means all clients.
 *
 * @param {string|string[]|null|undefined} client
 * @returns {string[]}
 */
function normalizeClientIds(client) {
  if (client == null || client === "") return [];
  const ids = Array.isArray(client) ? client : [client];
  return [
    ...new Set(
      ids
        .map((id) => (id == null ? "" : String(id)))
        .filter((id) => id !== ""),
    ),
  ];
}

const PAYOUT_STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "Pending", label: "Pending" },
  { value: "Approved", label: "Approved" },
  { value: "Completed", label: "Completed" },
  { value: "Cancelled", label: "Cancelled" },
];

const INVOICE_STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "Review", label: "Review" },
  { value: "Paid", label: "Paid" },
];

const PROFIT_LOSS_INVOICE_OPTIONS = [
  { value: "all", label: "All invoices (accrual)" },
  { value: "paid", label: "Paid invoices only (cash)" },
];

const PROFIT_LOSS_PAYOUT_OPTIONS = [
  { value: "all", label: "All payouts" },
  { value: "completed", label: "Completed payouts only" },
];

/** Default "what to include" for the Profit / Loss report. */
const DEFAULT_PROFIT_LOSS_OPTIONS = {
  includeInvoices: true,
  includePayouts: true,
  includeExpenses: false,
  invoiceStatus: "all",
  payoutStatus: "all",
};

const ReportsPage = () => {
  // URL search params for deep linking
  const [searchParams, setSearchParams] = useSearchParams();
  const queryTab = searchParams.get("tab") || "generate";
  const deepLinkedSavedReportId = searchParams.get("savedReportId");

  // Header ref for SidebarContainer
  const headerRef = React.useRef();
  const [headerContext, setHeaderContext] = useState();

  useEffect(() => {
    setHeaderContext(headerRef.current);
  }, [headerRef.current]);

  const renderHeader = () => {
    return <div ref={headerRef}></div>;
  };

  // State management
  const [activeTab, setActiveTab] = useState(
    searchParams.get("tab") || "generate",
  );
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [customFilters, setCustomFilters] = useState({});
  const [reportData, setReportData] = useState(null);
  const [lastReportRequest, setLastReportRequest] = useState(null); // Store query parameters for saving
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [showSaveReportModal, setShowSaveReportModal] = useState(false);
  const [saveReportDraft, setSaveReportDraft] = useState({
    name: "",
    description: "",
  });
  const [legAuditOpenRequest, setLegAuditOpenRequest] = useState(null);
  const [reportHistory, setReportHistory] = useState([]);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false); // Advanced settings collapsed by default
  const { user } = useAuth();

  // Form state for report generation
  const [formData, setFormData] = useState({
    modelName: searchParams.get("model") || "Payouts",
    dateRangeType: "monthly", // monthly, quarterly, yearly, custom
    dateRange: {
      startDate: null,
      endDate: null,
    },
    // For preset ranges - specific month/quarter/year selection
    selectedMonth: new Date(),
    selectedQuarter: getQuarter(new Date()),
    selectedYear: new Date().getFullYear(),
    status: "", // Defaults to "all" (empty string)
    employee: [], // Defaults to empty array (all employees)
    client: [], // Defaults to empty array (all clients). Legacy saved queries may still send a string id.
    customMatchConditions: {},
    parentCompany: user?.parentCompany,
    groupByPayDate: true, // Group payouts by payDate (default to true)
    profitLoss: DEFAULT_PROFIT_LOSS_OPTIONS, // Profit / Loss sections and status basis
  });

  // Hooks
  const {
    configs,
    parentCompany: parentCompanyId,
    loading: configLoading,
  } = useConfig();

  // Get parentCompany object with name if available (similar to DriverReports)
  const parentCompany = configs?.parentCompany || {
    _id: parentCompanyId || user?.parentCompany,
    name: configs?.companyName || "Company",
  };

  // customerConfig structure expected by the report generation: { parentCompany: string }
  const customerConfig = {
    parentCompany: parentCompanyId || user?.parentCompany,
  };
  const [generateReport, { isLoading: generatingReport, error: reportError }] =
    useGenerateReportMutation();
  const { data: templates, isLoading: loadingTemplates } =
    useGetReportTemplatesQuery();
  const [createTemplate] = useCreateReportTemplateMutation();
  const [saveReport, { isLoading: isSavingReport }] = useSaveReportMutation();

  // Fetch employees, organizations, and clients for dropdowns
  const { data: employeesData } = useGetEmployeesQuery(
    { limit: 100 },
    { skip: !["Manifests", "Payouts"].includes(formData.modelName) },
  );
  const { data: organizationsData } = useGetOrganizationsQuery(
    {},
    { skip: !["Manifests", "Payouts"].includes(formData.modelName) },
  );
  const { data: clientsData } = useGetClientsQuery(
    { limit: 100 },
    { skip: !["Jobs", "Legs", "Invoices"].includes(formData.modelName) },
  );

  // Transform data for select options
  // Combine employees and organizations in the employee dropdown
  const employeeOptions = React.useMemo(() => {
    const employees =
      employeesData?.employees?.map((employee) => ({
        label: employee?.fullName || "No Name",
        value: employee._id,
        type: "employee",
        ...employee,
      })) || [];

    const organizations =
      organizationsData?.map((org) => ({
        label: `${org.name || "Unnamed Organization"} (ORG)`,
        value: `org_${org._id}`, // Prefix to identify as organization
        type: "organization",
        organizationId: org._id,
        employeeIds:
          org.employees?.map((emp) =>
            typeof emp === "object" ? emp._id : emp,
          ) || [],
        ...org,
      })) || [];

    return [...organizations, ...employees];
  }, [employeesData, organizationsData]);

  const clientOptions =
    clientsData?.clients?.map((client) => ({
      label: client?.name || "Unnamed Client",
      value: client._id,
      ...client,
    })) || [];

  // Helper functions to get display labels for selected values
  const getEmployeeLabel = (employeeIds) => {
    if (
      !employeeIds ||
      (Array.isArray(employeeIds) && employeeIds.length === 0)
    ) {
      return "";
    }
    // Handle both array and single value for backward compatibility
    const ids = Array.isArray(employeeIds) ? employeeIds : [employeeIds];
    const labels = ids
      .map((id) => {
        const employee = employeeOptions.find((emp) => emp.value === id);
        return employee?.label || "";
      })
      .filter((label) => label !== "");
    return labels.length > 0 ? labels.join(", ") : "";
  };

  const getClientLabel = (clientId) => {
    const ids = normalizeClientIds(clientId);
    if (ids.length === 0) return "";
    const client = clientOptions.find((c) => c.value === ids[0]);
    return client?.label || "";
  };

  const getModelLabel = (modelName) => {
    if (!modelName) return "";
    const model = availableModels.find((m) => m.value === modelName);
    return model?.label || "";
  };

  // Calculate date range based on preset type
  const calculateDateRange = React.useCallback((type, month, quarter, year) => {
    let startDate, endDate;

    switch (type) {
      case "monthly":
        const monthDate =
          month instanceof Date
            ? month
            : new Date(
                year,
                (month instanceof Date ? month.getMonth() : month) - 1,
                1,
              );
        startDate = startOfMonth(monthDate);
        endDate = endOfMonth(monthDate);
        break;
      case "quarterly":
        // Calculate quarter start month (0-indexed)
        const quarterStartMonth = (quarter - 1) * 3;
        const quarterStart = new Date(year, quarterStartMonth, 1);
        const quarterEnd = new Date(year, quarterStartMonth + 2, 31);
        startDate = startOfMonth(quarterStart);
        endDate = endOfMonth(quarterEnd);
        break;
      case "yearly":
        const yearDate = new Date(year, 0, 1);
        startDate = startOfYear(yearDate);
        endDate = endOfYear(yearDate);
        break;
      case "custom":
        return null; // Don't calculate for custom
      case "allTime":
        return null; // Don't calculate for all time
      default:
        return { startDate: null, endDate: null };
    }

    return { startDate, endDate };
  }, []);

  // Update date range when preset type or specific selection changes
  useEffect(() => {
    if (formData.dateRangeType === "allTime") {
      // Clear date range for "all time"
      setFormData((prev) => ({
        ...prev,
        dateRange: {
          startDate: null,
          endDate: null,
        },
      }));
    } else if (formData.dateRangeType !== "custom") {
      const range = calculateDateRange(
        formData.dateRangeType,
        formData.selectedMonth,
        formData.selectedQuarter,
        formData.selectedYear,
      );
      if (range) {
        setFormData((prev) => ({
          ...prev,
          dateRange: range,
        }));
      }
    }
  }, [
    formData.dateRangeType,
    formData.selectedMonth,
    formData.selectedQuarter,
    formData.selectedYear,
    calculateDateRange,
  ]);

  // Initialize date range on mount
  useEffect(() => {
    if (formData.dateRangeType === "allTime") {
      // Clear date range for "all time"
      setFormData((prev) => ({
        ...prev,
        dateRange: {
          startDate: null,
          endDate: null,
        },
      }));
    } else if (formData.dateRangeType && formData.dateRangeType !== "custom") {
      const range = calculateDateRange(
        formData.dateRangeType,
        formData.selectedMonth,
        formData.selectedQuarter,
        formData.selectedYear,
      );
      if (range) {
        setFormData((prev) => ({
          ...prev,
          dateRange: range,
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ensure date range is always initialized for non-custom types
  useEffect(() => {
    if (formData.dateRangeType === "allTime") {
      // Clear date range for "all time"
      if (formData.dateRange.startDate || formData.dateRange.endDate) {
        setFormData((prev) => ({
          ...prev,
          dateRange: {
            startDate: null,
            endDate: null,
          },
        }));
      }
    } else if (
      formData.dateRangeType &&
      formData.dateRangeType !== "custom" &&
      (!formData.dateRange.startDate || !formData.dateRange.endDate)
    ) {
      const range = calculateDateRange(
        formData.dateRangeType,
        formData.selectedMonth,
        formData.selectedQuarter,
        formData.selectedYear,
      );
      if (range) {
        setFormData((prev) => ({
          ...prev,
          dateRange: range,
        }));
      }
    }
  }, [
    formData.dateRangeType,
    formData.selectedMonth,
    formData.selectedQuarter,
    formData.selectedYear,
    calculateDateRange,
  ]);

  // Generate month options for selection
  const monthOptions = Array.from({ length: 12 }, (_, i) => {
    const date = new Date(2000, i, 1);
    return {
      value: i + 1,
      label: format(date, "MMMM"),
    };
  });

  // Generate quarter options
  const quarterOptions = [
    { value: 1, label: "Q1 (Jan-Mar)" },
    { value: 2, label: "Q2 (Apr-Jun)" },
    { value: 3, label: "Q3 (Jul-Sep)" },
    { value: 4, label: "Q4 (Oct-Dec)" },
  ];

  // Generate year options (current year and previous 5 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => {
    const year = currentYear - i;
    return {
      value: year,
      label: year.toString(),
    };
  });

  // Available models for reports
  const availableModels = [
    { value: "Payouts", label: "Payouts" },
    // { value: "Manifests", label: "Manifests" },
    // { value: "Jobs", label: "Jobs" },
    // { value: "Legs", label: "Legs" },
    // { value: "Manifests", label: "Manifests" },
    // { value: "Shift", label: "Shifts" },
    { value: "Invoices", label: "Invoices" },
    { value: "ProfitLoss", label: "Profit / Loss" },
    // { value: "Client", label: "Clients" },
    // { value: "Maintenance", label: "Maintenance" },
    // { value: "Equipment", label: "Equipment" },
  ];

  // Keep tab state in sync when URL query changes (e.g. opening from Saved Reports).
  // IMPORTANT: only depend on queryTab to avoid clobbering user tab clicks with stale params.
  useEffect(() => {
    if (queryTab !== activeTab) {
      setActiveTab(queryTab);
    }
  }, [queryTab]);

  // One-time deep-link handoff for external URLs.
  useEffect(() => {
    if (deepLinkedSavedReportId && activeTab !== "legRouteAudit") {
      setActiveTab("legRouteAudit");
      setLegAuditOpenRequest({
        id: deepLinkedSavedReportId,
        token: Date.now(),
      });
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", "legRouteAudit");
        next.delete("savedReportId");
        return next;
      });
    }
  }, [deepLinkedSavedReportId, activeTab, setSearchParams]);

  // Handle template selection
  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setFormData((prev) => ({
      ...prev,
      modelName: getUiModelName(template.modelName),
      employee: [], // Reset to empty array (All) instead of null
      client: [], // Reset to empty array (All) instead of null
    }));
  };

  // Reset dependent filters when model changes
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      employee: [], // Reset to empty array (All) instead of null
      client: [], // Reset to empty array (All) instead of null
      status: "",
    }));
  }, [formData.modelName]);

  // Helper function to convert plural model name to singular for API
  const getApiModelName = (modelName) => {
    const modelNameMap = {
      Payouts: "Payout",
      Manifests: "Manifest",
      Jobs: "Job",
      Legs: "Leg",
      Invoices: "Invoice",
    };
    return modelNameMap[modelName] || modelName;
  };

  // Helper function to convert singular model name to plural for UI
  const getUiModelName = (modelName) => {
    const modelNameMap = {
      Payout: "Payouts",
      Manifest: "Manifests",
      Job: "Jobs",
      Leg: "Legs",
      Invoice: "Invoices",
    };
    return modelNameMap[modelName] || modelName;
  };

  // Check if all required fields are filled for report generation
  const isFormValid = () => {
    // Model is required
    if (!formData.modelName) return false;

    // Date range is required unless "allTime" is selected
    if (
      formData.dateRangeType !== "allTime" &&
      (!formData.dateRange.startDate || !formData.dateRange.endDate)
    ) {
      return false;
    }

    // Employee/Client must be selected (even if "All" - empty string is valid)
    // Only check if the field exists (not null/undefined), empty string means "All" which is valid
    if (["Manifests", "Payouts"].includes(formData.modelName)) {
      // employee is required, but can be [] for "All Employees"
      if (formData.employee === null || formData.employee === undefined)
        return false;
    }
    if (["Jobs", "Legs", "Invoices"].includes(formData.modelName)) {
      // client is required, but [] (or a legacy "") means "All Clients"
      if (formData.client === null || formData.client === undefined)
        return false;
    }

    // Profit / Loss needs at least one section to compile
    if (formData.modelName === "ProfitLoss") {
      const options = formData.profitLoss || DEFAULT_PROFIT_LOSS_OPTIONS;
      if (
        !options.includeInvoices &&
        !options.includePayouts &&
        !options.includeExpenses
      ) {
        return false;
      }
    }

    // Status is optional (defaults to "" which means "all")
    // So we don't need to check it

    return true;
  };

  /**
   * Builds the company Profit / Loss request. No client or employee filter:
   * the report covers the whole parent company for the selected period.
   * @returns {object} body for `POST /api/v1/reports`
   */
  const buildProfitLossRequest = () => {
    const options = formData.profitLoss || DEFAULT_PROFIT_LOSS_OPTIONS;
    const hasDates =
      formData.dateRangeType !== "allTime" &&
      formData.dateRange.startDate &&
      formData.dateRange.endDate;
    const toIso = (value) =>
      value instanceof Date ? value.toISOString() : new Date(value).toISOString();

    return {
      reportType: "profitLoss",
      modelName: "ProfitLoss",
      dateRange: hasDates
        ? {
            startDate: toIso(formData.dateRange.startDate),
            endDate: toIso(formData.dateRange.endDate),
          }
        : null,
      include: {
        invoices: Boolean(options.includeInvoices),
        payouts: Boolean(options.includePayouts),
        expenses: Boolean(options.includeExpenses),
      },
      invoiceStatus: options.invoiceStatus || "all",
      payoutStatus: options.payoutStatus || "all",
    };
  };

  /** Generates the company Profit / Loss report and stores it for saving and history. */
  const generateProfitLossReport = async () => {
    try {
      if (!(customerConfig?.parentCompany || user?.parentCompany)) {
        toast.error(
          "Customer configuration not loaded. Please wait and try again.",
        );
        return;
      }

      const reportRequest = buildProfitLossRequest();
      const reportMetadata = {
        dateRange: formData.dateRange,
        dateRangeType: formData.dateRangeType,
        selectedMonth: formData.selectedMonth,
        selectedQuarter: formData.selectedQuarter,
        selectedYear: formData.selectedYear,
      };
      setLastReportRequest({ ...reportRequest, reportMetadata });

      const response = await generateReport(reportRequest).unwrap();
      setReportData(response);

      setReportHistory((prev) => [
        {
          id: Date.now(),
          reportName: "Profit / Loss Report",
          modelName: "ProfitLoss",
          generatedAt: new Date(),
          recordCount: response?.data?.length || 0,
          request: reportRequest,
        },
        ...prev.slice(0, 9),
      ]);

      toast.success("Profit / Loss report generated.");
    } catch (error) {
      console.error("Profit / Loss report error:", error);
      toast.error(
        error?.data?.message || "Failed to generate profit / loss report",
      );
    }
  };

  // Generate report using legacy format (for existing functionality)
  const generateLegacyReport = async () => {
    try {
      // Check if customerConfig is loaded
      const parentCompany =
        customerConfig?.parentCompany || user?.parentCompany;
      if (!parentCompany) {
        toast.error(
          "Customer configuration not loaded. Please wait and try again.",
        );
        return;
      }

      const matchConditions = {
        parentCompany: parentCompany,
        ...formData.customMatchConditions,
      };

      // Build objectId fields array for config (fields that need ObjectId conversion)
      const objectIdFields = ["parentCompany"];

      // Ensure parentCompany is always in matchConditions (API overwrites it otherwise)
      // The API initially sets parentCompany from customerConfig, but then overwrites
      // matchConditions with stage.$match, so we need to ensure parentCompany is included

      // Add date range filter if specified (only for non-custom if dates exist)
      // Skip date filtering if "allTime" is selected
      if (
        formData.dateRangeType !== "allTime" &&
        formData.dateRange.startDate &&
        formData.dateRange.endDate &&
        (formData.dateRangeType === "custom" ||
          ["monthly", "quarterly", "yearly"].includes(formData.dateRangeType))
      ) {
        // Convert Date objects to ISO strings for proper serialization
        // Use payDate for Payouts, dateCreated for other models
        const dateField =
          formData.modelName === "Payouts" ? "payDate" : "dateCreated";
        matchConditions[dateField] = {
          $gte:
            formData.dateRange.startDate instanceof Date
              ? formData.dateRange.startDate.toISOString()
              : new Date(formData.dateRange.startDate).toISOString(),
          $lte:
            formData.dateRange.endDate instanceof Date
              ? formData.dateRange.endDate.toISOString()
              : new Date(formData.dateRange.endDate).toISOString(),
        };
      }

      // Add status filter if specified
      if (formData.status && formData.status !== "") {
        matchConditions.status = formData.status;
      }

      // Add employee/client filter based on model (only if not empty array - empty means "All")
      // Handle employee selection - can be array of IDs or array with organization IDs
      const employeeIds = Array.isArray(formData.employee)
        ? formData.employee
        : [];

      // Separate organization IDs from regular employee IDs
      const organizationIds = employeeIds.filter((id) =>
        id?.startsWith("org_"),
      );
      const regularEmployeeIds = employeeIds.filter(
        (id) => !id?.startsWith("org_"),
      );

      // Get all employee IDs from selected organizations
      // Use employeeOptions to get employeeIds - this matches what's shown in the dropdown
      const organizationEmployeeIds = organizationIds.flatMap((orgId) => {
        // Find the organization in employeeOptions to get its employeeIds
        const orgOption = employeeOptions.find(
          (option) => option.value === orgId && option.type === "organization",
        );

        // Use employeeIds from the option if available (this is what's shown in dropdown)
        if (orgOption?.employeeIds && orgOption.employeeIds.length > 0) {
          return orgOption.employeeIds.map((id) => String(id));
        }

        // Fallback: Get from organizationsData directly
        const organizationId = orgId.replace("org_", "");
        const org = organizationsData?.find(
          (org) => org._id === organizationId,
        );

        const orgEmployees =
          org?.employees?.map((emp) => {
            const id = typeof emp === "object" ? emp._id : emp;
            return String(id);
          }) || [];

        return orgEmployees;
      });

      // Combine all employee IDs (from organizations + direct selections)
      // Normalize all IDs to strings for consistent comparison and deduplication
      const allEmployeeIds = [
        ...regularEmployeeIds.map((id) => String(id)),
        ...organizationEmployeeIds,
      ];

      // Deduplicate the combined array
      const uniqueEmployeeIds = [...new Set(allEmployeeIds)];

      if (formData.modelName === "Manifests" && uniqueEmployeeIds.length > 0) {
        // Filter by selected employees
        matchConditions.driver = { $in: uniqueEmployeeIds };
        objectIdFields.push("driver");
      } else if (
        formData.modelName === "Payouts" &&
        uniqueEmployeeIds.length > 0
      ) {
        // Payouts can have either employee (service/repair) or driver (trucking)
        // Use the same logic for both manual selection and organization selection
        // Match by employee, driver, and drivers array fields
        matchConditions.$or = [
          { employee: { $in: uniqueEmployeeIds } },
          { driver: { $in: uniqueEmployeeIds } },
          { drivers: { $in: uniqueEmployeeIds } }, // Add drivers array support
        ];
        objectIdFields.push("employee", "driver", "drivers");
      } else if (["Jobs", "Legs", "Invoices"].includes(formData.modelName)) {
        // Empty selection means all clients. A legacy saved string id is still accepted.
        const clientIds = normalizeClientIds(formData.client);
        if (clientIds.length > 0) {
          matchConditions.client = { $in: clientIds };
          objectIdFields.push("client");
        }
      }

      // CRITICAL: Ensure parentCompany is always in matchConditions
      // The API replaces matchConditions with stage.$match, so we must include it
      matchConditions.parentCompany = parentCompany;

      // Use payDate for Payouts, dateCreated for other models
      const dateField =
        formData.modelName === "Payouts" ? "payDate" : "dateCreated";

      // Build stages array
      const stages = [{ $match: matchConditions }];

      // Add grouping by payDate if enabled for Payouts
      if (formData.groupByPayDate && formData.modelName === "Payouts") {
        // First, populate manifest data to get start/end dates before grouping
        stages.push({
          $lookup: {
            from: "manifests",
            localField: "manifestId",
            foreignField: "_id",
            as: "manifestData",
            pipeline: [
              {
                $project: {
                  _id: 1,
                  manifestId: 1,
                  startDate: 1,
                  endDate: 1,
                },
              },
            ],
          },
        });

        // Unwind manifest data (if exists) - keep it as an object
        stages.push({
          $addFields: {
            manifestInfo: {
              $cond: {
                if: { $gt: [{ $size: "$manifestData" }, 0] },
                then: { $arrayElemAt: ["$manifestData", 0] },
                else: null,
              },
            },
          },
        });

        // Group by payDate and calculate totals from nested arrays
        stages.push({
          $group: {
            _id: {
              $dateToString: {
                format: "%Y-%m-%d",
                date: "$payDate",
              },
            },
            payDate: { $first: "$payDate" },
            payouts: { $push: "$$ROOT" },
            totalPayouts: { $sum: 1 },
            // Calculate totals from nested arrays (legs and shifts)
            // These calculations happen within the group, so they work even if virtual fields aren't calculated yet
            totalAmount: {
              $sum: {
                $add: [
                  // Sum from legs (payoutTotal)
                  {
                    $sum: {
                      $map: {
                        input: { $ifNull: ["$legs", []] },
                        as: "leg",
                        in: {
                          $cond: {
                            if: {
                              $and: [
                                {
                                  $ne: [
                                    {
                                      $ifNull: [
                                        "$$leg.calculatedPayoutTotal",
                                        null,
                                      ],
                                    },
                                    null,
                                  ],
                                },
                              ],
                            },
                            then: {
                              $ifNull: ["$$leg.calculatedPayoutTotal", 0],
                            },
                            else: {
                              $cond: {
                                if: { $eq: ["$$leg.payoutType", "Flat"] },
                                then: {
                                  $add: [
                                    { $ifNull: ["$$leg.payoutRate", 0] },
                                    {
                                      $ifNull: ["$$leg.payoutSupplemental", 0],
                                    },
                                  ],
                                },
                                else: {
                                  $add: [
                                    {
                                      $cond: {
                                        if: {
                                          $and: [
                                            {
                                              $ne: [
                                                {
                                                  $ifNull: [
                                                    "$$leg.calculatedPayoutMileageTotal",
                                                    null,
                                                  ],
                                                },
                                                null,
                                              ],
                                            },
                                          ],
                                        },
                                        then: {
                                          $ifNull: [
                                            "$$leg.calculatedPayoutMileageTotal",
                                            0,
                                          ],
                                        },
                                        else: {
                                          $cond: {
                                            if: {
                                              $eq: ["$$leg.payoutType", "Flat"],
                                            },
                                            then: 0,
                                            else: {
                                              $multiply: [
                                                {
                                                  $ifNull: [
                                                    "$$leg.payoutRate",
                                                    0,
                                                  ],
                                                },
                                                {
                                                  $cond: {
                                                    if: {
                                                      $eq: [
                                                        "$$leg.useCustomManifestMileage",
                                                        true,
                                                      ],
                                                    },
                                                    then: {
                                                      $ifNull: [
                                                        "$$leg.customManifestMileage",
                                                        0,
                                                      ],
                                                    },
                                                    else: {
                                                      $ifNull: [
                                                        {
                                                          $ifNull: [
                                                            "$$leg.route.mileage",
                                                            "$$leg.route.defaultMileage",
                                                          ],
                                                        },
                                                        0,
                                                      ],
                                                    },
                                                  },
                                                },
                                              ],
                                            },
                                          },
                                        },
                                      },
                                    },
                                    {
                                      $ifNull: ["$$leg.payoutSupplemental", 0],
                                    },
                                  ],
                                },
                              },
                            },
                          },
                        },
                      },
                    },
                  },
                  // Sum from shifts
                  {
                    $sum: {
                      $map: {
                        input: { $ifNull: ["$shifts", []] },
                        as: "shift",
                        in: {
                          $add: [
                            { $ifNull: ["$$shift.payoutHourlyTotal", 0] },
                            { $ifNull: ["$$shift.payoutSupplemental", 0] },
                          ],
                        },
                      },
                    },
                  },
                ],
              },
            },
            totalMileage: {
              $sum: {
                $map: {
                  input: { $ifNull: ["$legs", []] },
                  as: "leg",
                  in: {
                    $cond: {
                      if: { $eq: ["$$leg.useCustomManifestMileage", true] },
                      then: { $ifNull: ["$$leg.customManifestMileage", 0] },
                      else: {
                        $ifNull: [
                          {
                            $ifNull: [
                              "$$leg.route.mileage",
                              "$$leg.route.defaultMileage",
                            ],
                          },
                          0,
                        ],
                      },
                    },
                  },
                },
              },
            },
            totalHours: {
              $sum: {
                $add: [
                  {
                    $divide: [
                      {
                        $sum: {
                          $map: {
                            input: { $ifNull: ["$legs", []] },
                            as: "leg",
                            in: { $ifNull: ["$$leg.waitTime.total", 0] },
                          },
                        },
                      },
                      3600000, // Convert milliseconds to hours
                    ],
                  },
                  {
                    $divide: [
                      {
                        $sum: {
                          $map: {
                            input: { $ifNull: ["$shifts", []] },
                            as: "shift",
                            in: { $ifNull: ["$$shift.totalWorkTime", 0] },
                          },
                        },
                      },
                      3600000, // Convert milliseconds to hours
                    ],
                  },
                ],
              },
            },
            totalMileageTotal: {
              $sum: {
                $map: {
                  input: { $ifNull: ["$legs", []] },
                  as: "leg",
                  in: {
                    $cond: {
                      if: {
                        $and: [
                          {
                            $ne: [
                              {
                                $ifNull: [
                                  "$$leg.calculatedPayoutMileageTotal",
                                  null,
                                ],
                              },
                              null,
                            ],
                          },
                        ],
                      },
                      then: {
                        $ifNull: ["$$leg.calculatedPayoutMileageTotal", 0],
                      },
                      else: {
                        $cond: {
                          if: { $eq: ["$$leg.payoutType", "Flat"] },
                          then: 0,
                          else: {
                            $multiply: [
                              { $ifNull: ["$$leg.payoutRate", 0] },
                              {
                                $cond: {
                                  if: {
                                    $eq: [
                                      "$$leg.useCustomManifestMileage",
                                      true,
                                    ],
                                  },
                                  then: {
                                    $ifNull: ["$$leg.customManifestMileage", 0],
                                  },
                                  else: {
                                    $ifNull: [
                                      {
                                        $ifNull: [
                                          "$$leg.route.mileage",
                                          "$$leg.route.defaultMileage",
                                        ],
                                      },
                                      0,
                                    ],
                                  },
                                },
                              },
                            ],
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
            totalHourlyTotal: {
              $sum: {
                $map: {
                  input: { $ifNull: ["$shifts", []] },
                  as: "shift",
                  in: { $ifNull: ["$$shift.payoutHourlyTotal", 0] },
                },
              },
            },
            totalSupplementalTotal: {
              $sum: {
                $add: [
                  {
                    $sum: {
                      $map: {
                        input: { $ifNull: ["$legs", []] },
                        as: "leg",
                        in: { $ifNull: ["$$leg.payoutSupplemental", 0] },
                      },
                    },
                  },
                  {
                    $sum: {
                      $map: {
                        input: { $ifNull: ["$shifts", []] },
                        as: "shift",
                        in: { $ifNull: ["$$shift.payoutSupplemental", 0] },
                      },
                    },
                  },
                ],
              },
            },
            // Collect manifest dates for payouts that have manifestInfo
            manifestStartDates: {
              $push: {
                $cond: {
                  if: { $ne: ["$manifestInfo", null] },
                  then: "$manifestInfo.startDate",
                  else: "$$REMOVE",
                },
              },
            },
            manifestEndDates: {
              $push: {
                $cond: {
                  if: { $ne: ["$manifestInfo", null] },
                  then: "$manifestInfo.endDate",
                  else: "$$REMOVE",
                },
              },
            },
            // Preserve other fields from first payout in group
            parentCompany: { $first: "$parentCompany" },
            status: { $first: "$status" },
          },
        });

        // Sort by payDate descending
        stages.push({
          $sort: { payDate: -1 },
        });

        // Unwind payouts array to show individual payouts within each group
        stages.push({
          $unwind: {
            path: "$payouts",
            preserveNullAndEmptyArrays: false,
          },
        });

        // Add fields to calculate min/max manifest dates for the group
        stages.push({
          $addFields: {
            _groupManifestStartDate: {
              $cond: {
                if: { $gt: [{ $size: "$manifestStartDates" }, 0] },
                then: { $min: "$manifestStartDates" },
                else: null,
              },
            },
            _groupManifestEndDate: {
              $cond: {
                if: { $gt: [{ $size: "$manifestEndDates" }, 0] },
                then: { $max: "$manifestEndDates" },
                else: null,
              },
            },
          },
        });

        // Replace root with the payout document but keep group totals and payDate
        // Note: $payouts was already unwound above, so we can directly access it
        stages.push({
          $replaceRoot: {
            newRoot: {
              $mergeObjects: [
                "$payouts",
                {
                  _groupPayDate: "$payDate", // Use the grouped payDate
                  _groupPayDateString: "$_id", // Keep the date string for sorting/display
                  _groupTotalPayouts: "$totalPayouts",
                  _groupTotalAmount: "$totalAmount",
                  _groupTotalMileage: "$totalMileage",
                  _groupTotalHours: "$totalHours",
                  _groupTotalMileageTotal: "$totalMileageTotal",
                  _groupTotalHourlyTotal: "$totalHourlyTotal",
                  _groupTotalSupplementalTotal: "$totalSupplementalTotal",
                  _groupManifestStartDate: "$_groupManifestStartDate",
                  _groupManifestEndDate: "$_groupManifestEndDate",
                },
              ],
            },
          },
        });
      }

      const reportRequest = {
        modelName: getApiModelName(formData.modelName),
        config: {
          0: {
            type: "$match",
            useDate: dateField,
            objectId: objectIdFields,
          },
        },
        stages: stages,
        populate: true,
        includeVirtuals: true,
      };

      // console.log("Report request:", JSON.stringify(reportRequest, null, 2));

      // Store the report request for saving query parameters
      setLastReportRequest({
        ...reportRequest,
        reportMetadata: {
          dateRange: formData.dateRange,
          dateRangeType: formData.dateRangeType,
          selectedMonth: formData.selectedMonth,
          selectedQuarter: formData.selectedQuarter,
          selectedYear: formData.selectedYear,
        },
      });

      const response = await generateReport(reportRequest).unwrap();
      // console.log("Report response:", response);
      // console.log(
      //   "Report data length:",
      //   response?.data?.length || response?.length || 0
      // );
      // console.log("Report response keys:", Object.keys(response || {}));

      // Handle different response structures
      let reportDataToSet = response;
      if (response?.data && Array.isArray(response.data)) {
        reportDataToSet = response;
      } else if (Array.isArray(response)) {
        reportDataToSet = { data: response };
      }

      setReportData(reportDataToSet);

      // Add to history
      const dataLength =
        reportDataToSet?.data?.length || reportDataToSet?.length || 0;
      const historyItem = {
        id: Date.now(),
        reportName: `${formData.modelName} Report`,
        modelName: formData.modelName,
        generatedAt: new Date(),
        recordCount: dataLength,
        filters: matchConditions,
      };

      setReportHistory((prev) => [historyItem, ...prev.slice(0, 9)]); // Keep last 10

      toast.success(
        `Report generated successfully! ${dataLength} records found.`,
      );
    } catch (error) {
      console.error("Report generation error:", error);
      console.error("Error details:", {
        message: error?.data?.message,
        status: error?.status,
        data: error?.data,
      });
      toast.error(error?.data?.message || "Failed to generate report");
    }
  };

  // Generate report using template
  const generateTemplateReport = async () => {
    if (!selectedTemplate) return;

    try {
      // Check if customerConfig is loaded
      const parentCompany =
        customerConfig?.parentCompany || user?.parentCompany;
      if (!parentCompany) {
        toast.error(
          "Customer configuration not loaded. Please wait and try again.",
        );
        return;
      }

      const matchConditions = {
        parentCompany: parentCompany,
        ...formData.customMatchConditions,
      };

      // Add date range filter (only for non-custom if dates exist)
      // Skip date filtering if "allTime" is selected
      if (
        formData.dateRangeType !== "allTime" &&
        formData.dateRange.startDate &&
        formData.dateRange.endDate &&
        (formData.dateRangeType === "custom" ||
          ["monthly", "quarterly", "yearly"].includes(formData.dateRangeType))
      ) {
        // Convert Date objects to ISO strings for proper serialization
        // Use payDate for Payouts, dateCreated for other models
        const dateField =
          formData.modelName === "Payouts" ? "payDate" : "dateCreated";
        matchConditions[dateField] = {
          $gte:
            formData.dateRange.startDate instanceof Date
              ? formData.dateRange.startDate.toISOString()
              : new Date(formData.dateRange.startDate).toISOString(),
          $lte:
            formData.dateRange.endDate instanceof Date
              ? formData.dateRange.endDate.toISOString()
              : new Date(formData.dateRange.endDate).toISOString(),
        };
      }

      // Add employee/client filter based on model (only if not empty array - empty means "All")
      // Handle employee selection - can be array of IDs or array with organization IDs
      const employeeIds = Array.isArray(formData.employee)
        ? formData.employee
        : [];

      // Separate organization IDs from regular employee IDs
      const organizationIds = employeeIds.filter((id) =>
        id?.startsWith("org_"),
      );
      const regularEmployeeIds = employeeIds.filter(
        (id) => !id?.startsWith("org_"),
      );

      // Get all employee IDs from selected organizations
      // Use employeeOptions to get employeeIds - this matches what's shown in the dropdown
      const organizationEmployeeIds = organizationIds.flatMap((orgId) => {
        // Find the organization in employeeOptions to get its employeeIds
        const orgOption = employeeOptions.find(
          (option) => option.value === orgId && option.type === "organization",
        );

        // Use employeeIds from the option if available (this is what's shown in dropdown)
        if (orgOption?.employeeIds && orgOption.employeeIds.length > 0) {
          return orgOption.employeeIds.map((id) => String(id));
        }

        // Fallback: Get from organizationsData directly
        const organizationId = orgId.replace("org_", "");
        const org = organizationsData?.find(
          (org) => org._id === organizationId,
        );

        const orgEmployees =
          org?.employees?.map((emp) => {
            const id = typeof emp === "object" ? emp._id : emp;
            return String(id);
          }) || [];

        return orgEmployees;
      });

      // Combine all employee IDs (from organizations + direct selections)
      // Normalize all IDs to strings for consistent comparison and deduplication
      const allEmployeeIds = [
        ...regularEmployeeIds.map((id) => String(id)),
        ...organizationEmployeeIds,
      ];

      // Deduplicate the combined array
      const uniqueEmployeeIds = [...new Set(allEmployeeIds)];

      if (formData.modelName === "Manifests" && uniqueEmployeeIds.length > 0) {
        // Filter by selected employees
        matchConditions.driver = { $in: uniqueEmployeeIds };
      } else if (
        formData.modelName === "Payouts" &&
        uniqueEmployeeIds.length > 0
      ) {
        // Payouts can have either employee (service/repair) or driver (trucking)
        // Use the same logic for both manual selection and organization selection
        // Match by employee, driver, and drivers array fields
        matchConditions.$or = [
          { employee: { $in: uniqueEmployeeIds } },
          { driver: { $in: uniqueEmployeeIds } },
          { drivers: { $in: uniqueEmployeeIds } }, // Add drivers array support
        ];
      } else if (["Jobs", "Legs", "Invoices"].includes(formData.modelName)) {
        const clientIds = normalizeClientIds(formData.client);
        if (clientIds.length > 0) {
          matchConditions.client = { $in: clientIds };
        }
      }

      const reportRequest = {
        reportId: selectedTemplate._id,
        matchConditions,
        additionalStages: [],
      };

      // console.log(
      //   "Template report request:",
      //   JSON.stringify(reportRequest, null, 2)
      // );

      // Store the report request for saving query parameters
      setLastReportRequest({
        ...reportRequest,
        reportMetadata: {
          dateRange: formData.dateRange,
          dateRangeType: formData.dateRangeType,
          selectedMonth: formData.selectedMonth,
          selectedQuarter: formData.selectedQuarter,
          selectedYear: formData.selectedYear,
        },
      });

      const response = await generateReport(reportRequest).unwrap();
      // console.log("Template report response:", response);
      // console.log(
      //   "Template report data length:",
      //   response?.data?.length || response?.length || 0
      // );
      // console.log(
      //   "Template report response keys:",
      //   Object.keys(response || {})
      // );

      // Handle different response structures
      let reportDataToSet = response;
      if (response?.data && Array.isArray(response.data)) {
        reportDataToSet = response;
      } else if (Array.isArray(response)) {
        reportDataToSet = { data: response };
      }

      setReportData(reportDataToSet);

      const dataLength =
        reportDataToSet?.data?.length || reportDataToSet?.length || 0;
      toast.success(
        `Template report generated successfully! ${dataLength} records found.`,
      );
    } catch (error) {
      console.error("Template report generation error:", error);
      console.error("Error details:", {
        message: error?.data?.message,
        status: error?.status,
        data: error?.data,
      });
      toast.error(error?.data?.message || "Failed to generate template report");
    }
  };

  // Handle form submission
  const handleGenerateReport = () => {
    if (formData.modelName === "ProfitLoss") {
      generateProfitLossReport();
    } else if (selectedTemplate) {
      generateTemplateReport();
    } else {
      generateLegacyReport();
    }
  };

  const handleSaveCurrentReport = async () => {
    if (!lastReportRequest) {
      toast.error("Generate a report first so we can save its query.");
      return;
    }

    const reportName = String(saveReportDraft?.name || "").trim();
    if (!reportName) {
      toast.error("Report name is required.");
      return;
    }

    try {
      await saveReport({
        reportName,
        modelName: getApiModelName(formData.modelName),
        description:
          String(saveReportDraft?.description || "").trim() ||
          `Saved ${formData.modelName} report query`,
        queryParameters: lastReportRequest,
        reportData: null,
        reportMetadata: {
          dateRange: formData.dateRange,
          dateRangeType: formData.dateRangeType,
          selectedMonth: formData.selectedMonth,
          selectedQuarter: formData.selectedQuarter,
          selectedYear: formData.selectedYear,
        },
        isQueryBased: true,
      }).unwrap();

      toast.success("Report saved successfully.");
      setShowSaveReportModal(false);
    } catch (error) {
      toast.error(error?.data?.message || "Failed to save report.");
    }
  };

  // Save current configuration as template
  const handleSaveAsTemplate = async () => {
    try {
      const templateData = {
        name: `${formData.modelName} Custom Report`,
        description: `Custom report for ${formData.modelName} with filters`,
        modelName: getApiModelName(formData.modelName),
        config: {
          populationRules: [], // Will be auto-generated based on model
          sortBy: { dateCreated: -1 },
          limit: 1000,
        },
        templateCategory: "custom",
        isTemplate: true,
      };

      await createTemplate(templateData).unwrap();
      toast.success("Report template created successfully!");
      setShowCreateTemplate(false);
    } catch (error) {
      toast.error("Failed to create template");
    }
  };

  /**
   * Updates one Profit / Loss option.
   * @param {string} key - Key of `formData.profitLoss`
   * @param {any} value
   */
  const setProfitLossOption = (key, value) =>
    setFormData((prev) => ({
      ...prev,
      profitLoss: {
        ...DEFAULT_PROFIT_LOSS_OPTIONS,
        ...(prev.profitLoss || {}),
        [key]: value,
      },
    }));

  /** "What to include" controls for the company Profit / Loss report. */
  const renderProfitLossOptions = () => {
    const options = formData.profitLoss || DEFAULT_PROFIT_LOSS_OPTIONS;
    const nothingIncluded =
      !options.includeInvoices &&
      !options.includePayouts &&
      !options.includeExpenses;

    return (
      <div className="border-t border-gray-200 pt-4 mb-6">
        <h4 className="text-sm font-medium text-gray-700 mb-1">
          What to include
        </h4>
        <p className="text-xs text-gray-500 mb-4">
          Covers the whole company for the selected date range. Invoices are
          dated by creation date, payouts by pay date. Cancelled and void
          records are left out.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Checkbox
              id="profitLossIncludeInvoices"
              checked={options.includeInvoices}
              onChange={(target) =>
                setProfitLossOption("includeInvoices", target.checked)
              }
              label="Invoices (revenue)"
            />
            {options.includeInvoices && (
              <Select
                value={
                  PROFIT_LOSS_INVOICE_OPTIONS.find(
                    (opt) => opt.value === options.invoiceStatus,
                  )?.label || PROFIT_LOSS_INVOICE_OPTIONS[0].label
                }
                onChange={(value) =>
                  setProfitLossOption("invoiceStatus", value.value || "all")
                }
                options={PROFIT_LOSS_INVOICE_OPTIONS}
              />
            )}
          </div>
          <div className="space-y-2">
            <Checkbox
              id="profitLossIncludePayouts"
              checked={options.includePayouts}
              onChange={(target) =>
                setProfitLossOption("includePayouts", target.checked)
              }
              label="Payouts (labor cost)"
            />
            {options.includePayouts && (
              <Select
                value={
                  PROFIT_LOSS_PAYOUT_OPTIONS.find(
                    (opt) => opt.value === options.payoutStatus,
                  )?.label || PROFIT_LOSS_PAYOUT_OPTIONS[0].label
                }
                onChange={(value) =>
                  setProfitLossOption("payoutStatus", value.value || "all")
                }
                options={PROFIT_LOSS_PAYOUT_OPTIONS}
              />
            )}
          </div>
          <div className="space-y-2">
            <Checkbox
              id="profitLossIncludeExpenses"
              checked={options.includeExpenses}
              onChange={(target) =>
                setProfitLossOption("includeExpenses", target.checked)
              }
              label="Expenses"
            />
            <p className="text-xs text-gray-500">
              Expense tracking is coming soon. Until then this section reports
              $0.
            </p>
          </div>
        </div>
        {nothingIncluded && (
          <p className="text-sm text-red-600 mt-3">
            Choose at least one section to include.
          </p>
        )}
      </div>
    );
  };

  // Render filter form
  const renderFilters = () => (
    <Card className="p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Report Filters</h3>

      {/* Top Section: Model, Status, Employee/Client */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {/* Model Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Data Model
          </label>
          <Select
            value={getModelLabel(formData.modelName)}
            onChange={(value) =>
              setFormData((prev) => ({ ...prev, modelName: value.value }))
            }
            options={availableModels}
            placeholder="Select model"
          />
        </div>

        {/* Employee Filter (for Manifests and Payouts) */}
        {["Manifests", "Payouts"].includes(formData.modelName) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Employee
            </label>
            <MultiSelect
              value={formData.employee}
              onChange={(selected) => {
                // MultiSelect returns array of selected items
                const selectedIds =
                  selected && selected.length > 0
                    ? selected.map((item) => item.value)
                    : [];
                setFormData((prev) => ({
                  ...prev,
                  employee: selectedIds,
                }));
              }}
              options={employeeOptions}
              placeholder="Search or select employees"
              search={true}
              searchKeys={[
                "fullName",
                "firstName",
                "lastName",
                "email",
                "name",
              ]}
            />
            {formData.employee.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {formData.employee.map((empId) => {
                  const emp = employeeOptions.find((e) => e.value === empId);
                  if (!emp) return null;

                  return (
                    <Badge
                      key={empId}
                      variant="primary"
                      className="inline-flex items-center gap-1"
                    >
                      <span>{emp.label}</span>
                      <button
                        type="button"
                        className="ml-1 hover:bg-primary-200 rounded px-0.5 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent any parent click handlers
                          e.preventDefault(); // Prevent default behavior
                          // Remove this specific employee, keep the others
                          const updatedIds = formData.employee.filter(
                            (id) => id !== empId,
                          );
                          setFormData((prev) => ({
                            ...prev,
                            employee: updatedIds,
                          }));
                        }}
                        aria-label={`Remove ${emp.label}`}
                      >
                        ×
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Client Filter (single select kept for Jobs and Legs) */}
        {["Jobs", "Legs"].includes(formData.modelName) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Client
            </label>
            <Select
              search
              searchKeys={["name", "email", "phone"]}
              value={getClientLabel(formData.client)}
              onChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  client: value.value ? [value.value] : [],
                }))
              }
              options={[{ value: "", label: "All Clients" }, ...clientOptions]}
              placeholder="Search or select client"
            />
          </div>
        )}

        {/* Client Filter (multi select for Invoices, same pattern as payout employees) */}
        {formData.modelName === "Invoices" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Client
            </label>
            <MultiSelect
              value={normalizeClientIds(formData.client)}
              onChange={(selected) => {
                const selectedIds =
                  selected && selected.length > 0
                    ? selected.map((item) => item.value)
                    : [];
                setFormData((prev) => ({
                  ...prev,
                  client: selectedIds,
                }));
              }}
              options={clientOptions}
              placeholder="Search or select clients"
              search={true}
              searchKeys={["name", "email", "phone"]}
            />
            {normalizeClientIds(formData.client).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {normalizeClientIds(formData.client).map((clientId) => {
                  const client = clientOptions.find(
                    (option) => option.value === clientId,
                  );
                  if (!client) return null;

                  return (
                    <Badge
                      key={clientId}
                      variant="primary"
                      className="inline-flex items-center gap-1"
                    >
                      <span>{client.label}</span>
                      <button
                        type="button"
                        className="ml-1 hover:bg-primary-200 rounded px-0.5 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          const updatedIds = normalizeClientIds(
                            formData.client,
                          ).filter((id) => id !== clientId);
                          setFormData((prev) => ({
                            ...prev,
                            client: updatedIds,
                          }));
                        }}
                        aria-label={`Remove ${client.label}`}
                      >
                        ×
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Status Filter (if applicable) */}
        {["Payouts", "Jobs", "Invoices"].includes(formData.modelName) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <Select
              value={
                formData.status
                  ? (formData.modelName === "Invoices"
                      ? INVOICE_STATUS_OPTIONS
                      : PAYOUT_STATUS_OPTIONS
                    ).find((opt) => opt.value === formData.status)?.label || ""
                  : ""
              }
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, status: value.value || "" }))
              }
              options={
                formData.modelName === "Invoices"
                  ? INVOICE_STATUS_OPTIONS
                  : PAYOUT_STATUS_OPTIONS
              }
              placeholder="Filter by status"
            />
          </div>
        )}
      </div>

      {/* Profit / Loss: company-wide, so choose sections instead of clients/employees */}
      {formData.modelName === "ProfitLoss" && renderProfitLossOptions()}

      {/* Bottom Section: Date Range */}
      <div className="border-t border-gray-200 pt-4">
        <h4 className="text-sm font-medium text-gray-700 mb-4">Date Range</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Date Range Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date Range Type
            </label>
            <Select
              value={
                formData.dateRangeType
                  ? [
                      { value: "allTime", label: "All Time" },
                      { value: "monthly", label: "Monthly" },
                      { value: "quarterly", label: "Quarterly" },
                      { value: "yearly", label: "Yearly" },
                      { value: "custom", label: "Custom Range" },
                    ].find((opt) => opt.value === formData.dateRangeType)
                      ?.label || ""
                  : ""
              }
              onChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  dateRangeType: value.value,
                }))
              }
              options={[
                { value: "allTime", label: "All Time" },
                { value: "monthly", label: "Monthly" },
                { value: "quarterly", label: "Quarterly" },
                { value: "yearly", label: "Yearly" },
                { value: "custom", label: "Custom Range" },
              ]}
              placeholder="Select date range type"
            />
          </div>

          {/* Month Selection (for Monthly) */}
          {formData.dateRangeType === "allTime" && (
            <div className="md:col-span-2">
              <p className="text-sm text-gray-600">
                All records will be included regardless of date.
              </p>
            </div>
          )}

          {formData.dateRangeType === "monthly" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Month
              </label>
              <Select
                value={
                  formData.selectedMonth instanceof Date
                    ? monthOptions.find(
                        (opt) =>
                          opt.value === formData.selectedMonth.getMonth() + 1,
                      )?.label || ""
                    : monthOptions.find(
                        (opt) => opt.value === formData.selectedMonth,
                      )?.label || ""
                }
                onChange={(value) =>
                  setFormData((prev) => {
                    const monthValue = value.value;
                    const currentYear = prev.selectedYear;
                    const newMonthDate = new Date(
                      currentYear,
                      monthValue - 1,
                      1,
                    );
                    return {
                      ...prev,
                      selectedMonth: newMonthDate,
                    };
                  })
                }
                options={monthOptions}
                placeholder="Select month"
              />
            </div>
          )}

          {/* Quarter Selection (for Quarterly) */}
          {formData.dateRangeType === "quarterly" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quarter
              </label>
              <Select
                value={
                  quarterOptions.find(
                    (opt) => opt.value === formData.selectedQuarter,
                  )?.label || ""
                }
                onChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    selectedQuarter: value.value,
                  }))
                }
                options={quarterOptions}
                placeholder="Select quarter"
              />
            </div>
          )}

          {/* Year Selection (for Monthly, Quarterly, and Yearly) */}
          {["monthly", "quarterly", "yearly"].includes(
            formData.dateRangeType,
          ) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Year
              </label>
              <Select
                value={
                  yearOptions.find((opt) => opt.value === formData.selectedYear)
                    ?.label || ""
                }
                onChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    selectedYear: value.value,
                  }))
                }
                options={yearOptions}
                placeholder="Select year"
              />
            </div>
          )}

          {/* Custom Date Range Picker */}
          {formData.dateRangeType === "custom" && (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Custom Date Range
              </label>
              <DateRangePicker
                startDate={formData.dateRange.startDate}
                endDate={formData.dateRange.endDate}
                onChange={(range) => {
                  const [start, end] = range;
                  setFormData((prev) => ({
                    ...prev,
                    dateRange: {
                      startDate: start,
                      endDate: end,
                    },
                  }));
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Advanced Settings (for Payouts) */}
      {formData.modelName === "Payouts" && (
        <div className="border-t border-gray-200 pt-4 mt-4">
          <button
            type="button"
            onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
            className="flex items-center justify-end gap-2 w-full text-right hover:text-gray-900 transition-colors"
          >
            <span className="text-sm font-medium text-gray-700">
              Advanced Settings
            </span>
            {showAdvancedSettings ? (
              <ChevronUpIcon className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDownIcon className="h-5 w-5 text-gray-500" />
            )}
          </button>
          {showAdvancedSettings && (
            <div className="mt-4 pl-4 border-l-2 border-gray-200">
              <div className="flex items-center">
                <Checkbox
                  id="groupByPayDate"
                  checked={formData.groupByPayDate}
                  onChange={(target) =>
                    setFormData((prev) => ({
                      ...prev,
                      groupByPayDate: target.checked,
                    }))
                  }
                  label="Group by Pay Date"
                />
                <p className="text-sm text-gray-500 ml-4">
                  Group payouts by their pay date to see totals per day
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-between mt-6">
        {configLoading && (
          <div className="text-sm text-gray-600 mb-2">
            Loading customer configuration...
          </div>
        )}
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            onClick={() => {
              setSaveReportDraft({
                name: `${getModelLabel(formData.modelName) || formData.modelName} Report ${format(
                  new Date(),
                  "yyyy-MM-dd HH:mm",
                )}`,
                description: "",
              });
              setShowSaveReportModal(true);
            }}
            disabled={!lastReportRequest}
            className="flex items-center"
          >
            <BookmarkIcon className="h-4 w-4 mr-1" />
            Save Report
          </Button>
          <Button
            onClick={handleGenerateReport}
            disabled={
              generatingReport ||
              configLoading ||
              !(customerConfig?.parentCompany || user?.parentCompany) ||
              !isFormValid()
            }
            className="flex items-center"
          >
            <PlayIcon className="h-4 w-4 mr-1" />
            {generatingReport
              ? "Generating..."
              : configLoading
                ? "Loading Config..."
                : "Generate Report"}
          </Button>

          {/* <Button
            variant="outline"
            onClick={() => setShowCreateTemplate(true)}
            className="flex items-center"
          >
            <BookmarkIcon className="h-4 w-4 mr-1" />
            Save as Template
          </Button> */}
        </div>

        {selectedTemplate && (
          <Badge variant="blue" className="flex items-center">
            <DocumentChartBarIcon className="h-3 w-3 mr-1" />
            Using: {selectedTemplate.name}
          </Badge>
        )}
      </div>
    </Card>
  );

  // Render templates tab
  const renderTemplatesTab = () => (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            Report Templates
          </h3>
          <Button
            onClick={() => setShowCreateTemplate(true)}
            size="sm"
            className="flex items-center"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            New Template
          </Button>
        </div>

        {loadingTemplates ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-600">Loading templates...</p>
          </div>
        ) : templates && Array.isArray(templates) && templates.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template) => (
              <div
                key={template._id}
                className={`p-4 border rounded-lg cursor-pointer transition-all ${
                  selectedTemplate?._id === template._id
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                onClick={() => handleTemplateSelect(template)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">
                      {template.name}
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      {template.description}
                    </p>
                    <div className="flex items-center mt-2 space-x-2">
                      <Badge variant="gray" size="sm">
                        {template.modelName}
                      </Badge>
                      <Badge variant="blue" size="sm">
                        {template.templateCategory}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <DocumentChartBarIcon className="h-12 w-12 text-gray-400 mx-auto" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900">
              No templates
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Create your first report template to get started.
            </p>
          </div>
        )}
      </Card>
    </div>
  );

  // Handle regenerating from history
  const handleRegenerateFromHistory = async (historyItem) => {
    if (historyItem?.request?.reportType === "profitLoss") {
      try {
        const response = await generateReport(historyItem.request).unwrap();
        setReportData(response);
        setActiveTab("generate");
        toast.success("Profit / Loss report regenerated.");
      } catch (error) {
        toast.error(error?.data?.message || "Failed to regenerate report");
      }
      return;
    }

    try {
      // Reconstruct the report request from history filters
      const parentCompany =
        customerConfig?.parentCompany || user?.parentCompany;
      if (!parentCompany) {
        toast.error(
          "Customer configuration not loaded. Please wait and try again.",
        );
        return;
      }

      // Use the stored filters from history to regenerate
      const matchConditions = {
        parentCompany: parentCompany,
        ...(historyItem.filters || {}),
      };

      const reportRequest = {
        modelName: getApiModelName(historyItem.modelName),
        config: {
          0: {
            type: "$match",
            useDate:
              historyItem.modelName === "Payouts" ? "payDate" : "dateCreated",
            objectId: ["parentCompany"],
          },
        },
        stages: [{ $match: matchConditions }],
        populate: true,
        includeVirtuals: true,
      };

      const response = await generateReport(reportRequest).unwrap();

      // Handle different response structures
      let reportDataToSet = response;
      if (response?.data && Array.isArray(response.data)) {
        reportDataToSet = response;
      } else if (Array.isArray(response)) {
        reportDataToSet = { data: response };
      }

      setReportData(reportDataToSet);
      setActiveTab("generate"); // Switch to generate tab to show results

      toast.success(
        `Report regenerated successfully! ${
          reportDataToSet?.data?.length || reportDataToSet?.length || 0
        } records found.`,
      );
    } catch (error) {
      console.error("Error regenerating report from history:", error);
      toast.error(error?.data?.message || "Failed to regenerate report");
    }
  };

  // Render history tab
  const renderHistoryTab = () => (
    <Card className="p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Reports</h3>

      {reportHistory &&
      Array.isArray(reportHistory) &&
      reportHistory.length > 0 ? (
        <div className="space-y-3">
          {reportHistory.map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between p-3 border rounded-lg"
            >
              <div>
                <h4 className="font-medium text-gray-900">{item.reportName}</h4>
                <p className="text-sm text-gray-600">
                  {item.recordCount} records •{" "}
                  {item.generatedAt instanceof Date
                    ? item.generatedAt.toLocaleDateString()
                    : new Date(item.generatedAt).toLocaleDateString()}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleRegenerateFromHistory(item)}
                disabled={generatingReport}
              >
                {generatingReport ? "Regenerating..." : "Regenerate"}
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <ClockIcon className="h-12 w-12 text-gray-400 mx-auto" />
          <h3 className="mt-2 text-sm font-semibold text-gray-900">
            No recent reports
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Your generated reports will appear here.
          </p>
        </div>
      )}
    </Card>
  );

  // Render saved reports tab
  const renderSavedReportsTab = () => {
    const SavedReports = React.lazy(() => import("./SavedReports"));

    return (
      <React.Suspense
        fallback={<div className="text-center py-8">Loading...</div>}
      >
        <SavedReports
          onOpenLegRouteAudit={(reportId) => {
            setActiveTab("legRouteAudit");
            setLegAuditOpenRequest({
              id: reportId,
              token: Date.now(),
            });
            setSearchParams((prev) => ({
              ...Object.fromEntries(prev.entries()),
              tab: "legRouteAudit",
            }));
          }}
        />
      </React.Suspense>
    );
  };

  const renderLegRouteAuditTab = () => (
    <LegRouteAudit
      embedded
      savedReportIdToLoad={legAuditOpenRequest?.id || null}
      savedReportRequestToken={legAuditOpenRequest?.token || null}
      onSavedReportApplied={() => setLegAuditOpenRequest(null)}
    />
  );

  const tabs = [
    { id: "generate", title: "Generate Report", content: renderFilters() },
    {
      id: "legRouteAudit",
      title: "Leg Route Audit",
      content: renderLegRouteAuditTab(),
    },
    { id: "saved", title: "Saved Reports", content: renderSavedReportsTab() },
    // { id: "templates", title: "Templates", content: renderTemplatesTab() },
    // { id: "history", title: "History", content: renderHistoryTab() },
  ];

  return (
    <SidebarContainer header={renderHeader} hideSidebar className="w-full">
      {({ parentHeight }) => {
        const offsetTop = headerContext?.offsetTop || 0;
        const offsetHeight = headerContext?.offsetHeight || 0;
        const wrapperStyle = {
          height: `${parentHeight - offsetTop - offsetHeight}px`,
        };

        return (
          <div
            style={wrapperStyle}
            className="px-3 pb-3 overflow-y-auto w-full"
          >
            <div className="space-y-6 py-6">
              {/* Page Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
                  <p className="text-gray-600">
                    Generate comprehensive reports with flexible filtering and
                    export options.
                  </p>
                </div>
              </div>

              {/* Error Alert */}
              {reportError && (
                <Alert
                  type="error"
                  title="Report Generation Failed"
                  message={
                    reportError?.data?.message ||
                    "An error occurred while generating the report."
                  }
                  onDismiss={() => {
                    /* Handle error dismissal */
                  }}
                />
              )}

              {/* Tabs */}
              <Tabs
                data={tabs}
                default={0}
                selectedId={activeTab}
                onClick={(e, item) => {
                  const nextTab = item.id;
                  setActiveTab(nextTab);
                  setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    next.set("tab", nextTab);
                    next.delete("savedReportId");
                    return next;
                  });
                }}
              />

              {reportData?.reportType === "profitLoss" && activeTab === "generate" && (
                <ProfitLossReport
                  report={reportData}
                  reportName="Profit / Loss Report"
                  parentCompany={parentCompany}
                  loading={generatingReport}
                  onRefresh={() => handleGenerateReport()}
                />
              )}
              {activeTab === "generate" &&
                reportData?.reportType !== "profitLoss" &&
                reportData?.analytics && (
                  <InvoiceAnalyticsPanel analytics={reportData.analytics} />
                )}
              {reportData &&
                reportData.reportType !== "profitLoss" &&
                activeTab === "generate" && (
                <ReportTable
                  data={reportData.data || reportData}
                  analytics={reportData.analytics}
                  reportName={
                    selectedTemplate?.name || `${formData.modelName} Report`
                  }
                  reportConfig={
                    selectedTemplate || { modelName: formData.modelName }
                  }
                  loading={generatingReport}
                  onRefresh={() => handleGenerateReport()}
                  showSummary={true}
                  allowExport={true}
                  parentCompany={parentCompany}
                  reportMetadata={{
                    dateRange: formData.dateRange,
                    dateRangeType: formData.dateRangeType,
                    selectedMonth: formData.selectedMonth,
                    selectedQuarter: formData.selectedQuarter,
                    selectedYear: formData.selectedYear,
                  }}
                  reportRequest={lastReportRequest} // Pass query parameters for saving
                />
              )}

              {/* Create Template Modal */}
              <Modal
                isOpen={showCreateTemplate}
                onClose={() => setShowCreateTemplate(false)}
                title="Save as Template"
                size="md"
              >
                <div className="space-y-4">
                  <p className="text-gray-600">
                    Save the current report configuration as a reusable
                    template.
                  </p>

                  <div className="flex justify-end space-x-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowCreateTemplate(false)}
                    >
                      Cancel
                    </Button>
                    <Button onClick={handleSaveAsTemplate}>
                      Save Template
                    </Button>
                  </div>
                </div>
              </Modal>

              <Modal
                visible={showSaveReportModal}
                onClose={() => setShowSaveReportModal(false)}
                title="Save Report"
                size="md"
                footer={
                  <div className="flex justify-end space-x-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowSaveReportModal(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSaveCurrentReport}
                      loading={isSavingReport}
                    >
                      Save
                    </Button>
                  </div>
                }
              >
                <div className="space-y-4">
                  <Input
                    label="Report Name"
                    value={saveReportDraft.name}
                    onChange={(target) =>
                      setSaveReportDraft((prev) => ({
                        ...prev,
                        name: target?.value || "",
                      }))
                    }
                  />
                  <Input
                    label="Description"
                    value={saveReportDraft.description}
                    onChange={(target) =>
                      setSaveReportDraft((prev) => ({
                        ...prev,
                        description: target?.value || "",
                      }))
                    }
                  />
                </div>
              </Modal>
            </div>
          </div>
        );
      }}
    </SidebarContainer>
  );
};

export default ReportsPage;
